import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { certificatesApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Certificate,
  MagnifyingGlass,
  CheckCircle,
  XCircle,
  User,
  Buildings,
  Chalkboard,
  Calendar,
  CircleNotch,
  ShieldCheck
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gm-class-training/artifacts/1szu61lb_Logo%20G%26M.png";

export default function ValidateCertificatePage() {
  const { code: urlCode } = useParams();
  const [code, setCode] = useState(urlCode || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const doValidate = async (codeToValidate) => {
    if (!codeToValidate.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await certificatesApi.validate(codeToValidate.trim());
      setResult(res.data);
    } catch {
      setResult({ is_valid: false, code: codeToValidate.trim() });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (urlCode) {
      setCode(urlCode);
      doValidate(urlCode);
    }
  }, [urlCode]);

  const handleValidate = (e) => {
    e.preventDefault();
    doValidate(code);
  };

  const isExpired = result?.expiry_date && new Date(result.expiry_date) < new Date();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#2B4B8C]/5 to-[#f8fafc]" data-testid="validate-certificate-page">
      {/* Header */}
      <header className="bg-white border-b border-[#2B4B8C]/10 shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-center">
          <img src={LOGO_URL} alt="G&M OTEC" className="h-9 object-contain" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Title */}
        <div className="text-center mb-5">
          <div className="w-12 h-12 rounded-full bg-[#2BBAA4]/10 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck size={24} className="text-[#2BBAA4]" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#2B4B8C]">Validar Certificado</h1>
          <p className="text-sm text-[#2B4B8C]/50 mt-1">
            Ingresa el codigo para verificar autenticidad
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleValidate} className="mb-5">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/30" size={18} />
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Codigo del certificado"
                className="pl-10 h-12 bg-white border-[#2B4B8C]/15 text-[#2B4B8C] font-mono text-sm placeholder:font-sans placeholder:text-[#2B4B8C]/35"
                data-testid="certificate-code-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !code.trim()}
              className="h-12 px-5 bg-[#2BBAA4] text-white hover:bg-[#239986] font-semibold shrink-0"
              data-testid="validate-btn"
            >
              {loading ? <CircleNotch className="animate-spin" size={18} /> : 'Validar'}
            </Button>
          </div>
        </form>

        {/* Result */}
        {searched && result && (
          <>
            {result.is_valid ? (
              <Card className="bg-white border-[#2B4B8C]/10 shadow-lg overflow-hidden animate-fade-in" data-testid="certificate-result">
                {/* Status Banner */}
                <div className="bg-green-500 px-4 py-3 flex items-center gap-2.5">
                  <CheckCircle size={22} className="text-white" weight="fill" />
                  <div className="flex-1">
                    <p className="text-white font-semibold text-sm">Certificado Valido</p>
                    <p className="text-white/80 text-xs">Documento verificado y autentico</p>
                  </div>
                  <Badge className="bg-white/20 text-white border-white/30 border text-xs">
                    Verificado
                  </Badge>
                </div>

                <CardContent className="p-4 space-y-3">
                  {/* Worker */}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/5">
                    <div className="w-9 h-9 rounded-full bg-[#2B4B8C]/8 flex items-center justify-center shrink-0 mt-0.5">
                      <User size={18} className="text-[#2B4B8C]/60" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-[#2B4B8C]/40 font-medium">Trabajador</p>
                      <p className="text-[#2B4B8C] font-semibold text-sm">{result.worker_name}</p>
                      {result.worker_rut && (
                        <p className="text-xs text-[#2B4B8C]/50">RUT: {result.worker_rut}</p>
                      )}
                    </div>
                  </div>

                  {/* Company */}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/5">
                    <div className="w-9 h-9 rounded-full bg-[#2B4B8C]/8 flex items-center justify-center shrink-0 mt-0.5">
                      <Buildings size={18} className="text-[#2B4B8C]/60" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-[#2B4B8C]/40 font-medium">Empresa</p>
                      <p className="text-[#2B4B8C] font-semibold text-sm">{result.company_name || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Training */}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/5">
                    <div className="w-9 h-9 rounded-full bg-[#2BBAA4]/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Chalkboard size={18} className="text-[#2BBAA4]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wider text-[#2B4B8C]/40 font-medium">Capacitacion</p>
                      <p className="text-[#2B4B8C] font-semibold text-sm">{result.training_name}</p>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {result.training_type && (
                          <Badge className="bg-[#2B4B8C]/8 text-[#2B4B8C]/70 border-0 text-[10px] px-2 py-0.5">{result.training_type}</Badge>
                        )}
                        {result.modality && (
                          <Badge className="bg-[#2BBAA4]/10 text-[#2BBAA4] border-0 text-[10px] px-2 py-0.5">{result.modality}</Badge>
                        )}
                        {result.methodology && (
                          <Badge className="bg-[#2B4B8C]/8 text-[#2B4B8C]/60 border-0 text-[10px] px-2 py-0.5">{result.methodology}</Badge>
                        )}
                      </div>
                      {result.client_name_mandante && (
                        <p className="text-xs text-[#2B4B8C]/50 mt-1.5">Mandante: <span className="font-medium text-[#2B4B8C]/70">{result.client_name_mandante}</span></p>
                      )}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-3 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Calendar size={12} className="text-[#2B4B8C]/40" />
                        <p className="text-[10px] uppercase tracking-wider text-[#2B4B8C]/40 font-medium">Emision</p>
                      </div>
                      <p className="text-[#2B4B8C] font-medium text-xs">
                        {result.issued_date ? format(new Date(result.issued_date), "dd MMM yyyy", { locale: es }) : '-'}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg border ${isExpired ? 'bg-red-50 border-red-200' : 'bg-[#f8fafc] border-[#2B4B8C]/5'}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <Calendar size={12} className={isExpired ? 'text-red-400' : 'text-[#2B4B8C]/40'} />
                        <p className={`text-[10px] uppercase tracking-wider font-medium ${isExpired ? 'text-red-400' : 'text-[#2B4B8C]/40'}`}>Vigencia</p>
                      </div>
                      <p className={`font-medium text-xs ${isExpired ? 'text-red-600' : 'text-[#2B4B8C]'}`}>
                        {result.expiry_date ? format(new Date(result.expiry_date), "dd MMM yyyy", { locale: es }) : 'Sin vencimiento'}
                      </p>
                      {isExpired && (
                        <Badge className="mt-1 bg-red-500/10 text-red-500 border-0 text-[10px] px-1.5 py-0">Vencido</Badge>
                      )}
                    </div>
                  </div>

                  {/* Code */}
                  <div className="p-3 rounded-lg bg-[#2BBAA4]/5 border border-[#2BBAA4]/15 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-[#2B4B8C]/40 font-medium mb-0.5">Codigo de Verificacion</p>
                    <p className="text-base font-mono text-[#2BBAA4] font-bold tracking-wider">{result.code}</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-red-200 shadow-lg overflow-hidden animate-fade-in" data-testid="certificate-result-invalid">
                <div className="bg-red-500 px-4 py-3 flex items-center gap-2.5">
                  <XCircle size={22} className="text-white" weight="fill" />
                  <div>
                    <p className="text-white font-semibold text-sm">Certificado No Valido</p>
                    <p className="text-white/80 text-xs">No se encontro certificado con este codigo</p>
                  </div>
                </div>
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-[#2B4B8C]/60">
                    Codigo buscado: <span className="font-mono font-medium text-[#2B4B8C]">{result.code}</span>
                  </p>
                  <p className="text-xs text-[#2B4B8C]/40 mt-2">Verifica que el codigo sea correcto e intenta nuevamente</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Footer info */}
        <p className="mt-6 text-center text-xs text-[#2B4B8C]/35 px-4">
          Certificados emitidos por G&M OTEC. Codigo unico de 16 caracteres.
        </p>
      </main>
    </div>
  );
}
